import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { CreateUserFormType } from "@/pages/users/components/table/components/userCreateDialog";
import { UserType } from "@/types/user.types";

export const useDeleteUserMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string }>({
        mutationFn: async ({ id }) => {
            try {
                const res = await axiosInstance.delete(`/users/${id}`).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};

export const useRestoreUserMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string; identityNo: string }>({
        mutationFn: async ({ id, identityNo }) => {
            try {
                const res = await axiosInstance.patch(`/users/${id}/restore`, { identityNo }).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};

export const useUpdateUserLifeCycleMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<
        { id: string; ok: boolean; message: string },
        Error,
        { id: string; startedAt: DateTime; lifecycleId: string; leavedAt?: DateTime | undefined | null }
    >({
        mutationFn: async ({ id, startedAt, leavedAt, lifecycleId }) => {
            try {
                const payload: { startedAt: string | null; leavedAt?: string | null } = {
                    startedAt: startedAt.toISO(),
                };
                if (leavedAt) {
                    payload.leavedAt = leavedAt.toISO();
                }
                const res = await axiosInstance
                    .patch(`/users/${id}/lifecycle/${lifecycleId}`, payload)
                    .then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};

export const useUpdateUserShiftTypeMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string; isDisabled: boolean }>({
        mutationFn: async ({ id, isDisabled }) => {
            try {
                const res = await axiosInstance.patch(`/users/${id}/shift-type/${isDisabled}`).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};
export const useUpdateWetrackDataMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error>({
        mutationFn: async () => {
            try {
                const res = await axiosInstance.put(`/drivers/wetrack`).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};
export const useUpdateUserVehicleMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string; vehicleId: number | null }>({
        mutationFn: async ({ id, vehicleId }) => {
            try {
                const res = await axiosInstance.patch(`/users/${id}/vehicle/${vehicleId}`).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};

export const useUpdateUserUserTypeMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string; userType: UserType }>({
        mutationFn: async ({ id, userType }) => {
            try {
                const res = await axiosInstance.patch(`/users/${id}/type/${userType}`).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};
export const useUpdateUserPasswordMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string; password: string }>({
        mutationFn: async ({ id, password }) => {
            try {
                const res = await axiosInstance.patch(`/users/${id}/password/`, { password }).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
    });
};

export const useUpdateUserLoginStatusMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, { id: string; disabled: boolean }>({
        mutationFn: async ({ id, disabled }) => {
            try {
                const res = await axiosInstance.patch(`/users/${id}/login/${disabled}`).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};

export const useCreateUserMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, CreateUserFormType>({
        mutationFn: async (payload) => {
            try {
                const data = JSON.parse(JSON.stringify(payload));
                if (data.userType !== UserType.DRIVER) {
                    delete data.identityNo;
                }
                if (data.userType !== UserType.WELCOME_TEAM_OFFICER) {
                    delete data.authorizedAirport;
                }
                const res = await axiosInstance
                    .post(`/users/`, {
                        ...data,
                        userName: data.userName.trim(),
                        name: data.name.trim(),
                        surname: data.surname.trim(),
                        authorizedAirport: data.airport,
                    })
                    .then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_USERS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS],
            });
        },
    });
};
